import Phaser from 'phaser';
import type { RunStats, WaveDef } from '../core/types';
import { gameEvents } from '../core/events';
import { Rng } from '../core/rng';
import { TEX } from '../core/TextureFactory';
import { audio } from '../core/AudioManager';
import { Hero } from './Hero';
import { Enemy } from './Enemy';
import { DamageNumbers } from './DamageNumbers';
import { BossController } from './BossController';
import { buildWaveSpawnPlan, spawnDelayMs, type SpawnInstance } from './logic/waveSpawns';
import { resolveEnemyAttack, resolveHeroAttack, computeLifesteal } from './logic/damage';
import { ballsDropped, bossBallsDropped } from './logic/drops';
import type { CombatRect } from './types';

const HERO_BASE_DAMAGE = 8;
const HERO_ATTACK_INTERVAL_MS = 700;
const ENEMY_ATTACK_INTERVAL_MS = 1200;

type EngineState = 'active' | 'collecting' | 'cleared' | 'dead';

interface Pickup {
  image: Phaser.GameObjects.Image;
  order: number;
}

export interface WaveEngineOptions {
  def: WaveDef;
  stats: RunStats;
  hp: number;
  maxHp: number;
  wave: number;
}

/** Textura del jefe según el bioma en su id (`boss-bosque`, `boss-oeste`, ...); cae al jefe genérico. */
function bossTextureForType(type: string): string {
  if (type.includes('bosque')) return TEX.bossBosque;
  if (type.includes('oeste')) return TEX.bossOeste;
  if (type.includes('cripta')) return TEX.bossCripta;
  if (type.includes('volcan')) return TEX.bossVolcan;
  return TEX.boss;
}

function textureForType(type: string): string {
  if (type.includes('boss')) return bossTextureForType(type);
  if (type.includes('goblin')) return TEX.enemyGoblin;
  if (type.includes('bat')) return TEX.enemyBat;
  if (type.includes('spider')) return TEX.enemySpider;
  return TEX.enemySlime;
}

/** Nombre de despliegue simple para la barra de jefe, a partir de su id de tipo. */
function bossDisplayName(type: string): string {
  const cleaned = type.replace(/boss-?/i, '').replace(/-/g, ' ').trim();
  const label = cleaned.length > 0 ? cleaned : 'final';
  return `Jefe ${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

/**
 * Orquesta una oleada completa: aparición escalonada de enemigos (según el
 * plan de `combat/logic/waveSpawns`), marcha y melee de cada uno, autofire
 * del héroe (con la matemática de `combat/logic/damage`), drops de bolas al
 * morir y la fase de recolección tras limpiar la oleada. Toda la
 * comunicación hacia fuera es vía `gameEvents` (`combat:*`).
 *
 * `update(deltaMs)` es el único punto de entrada por frame — quien la
 * instancia (aquí, `CombatView`) ya se encarga de escalar `deltaMs` según
 * la velocidad ×1/×2 antes de llamarla.
 */
export class WaveEngine {
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private readonly wave: number;
  private readonly isBossWave: boolean;

  private readonly hero: Hero;
  private readonly damageNumbers: DamageNumbers;
  private bossController?: BossController;
  private stats: RunStats;
  private readonly rng: Rng;

  private pendingSpawns: SpawnInstance[];
  private elapsedMs = 0;
  private enemies: Enemy[] = [];
  private pickups: Pickup[] = [];
  private state: EngineState = 'active';

  private collectElapsedMs = 0;
  private collectDurationMs = 0;
  private readonly heroIdleX: number;
  private readonly heroCollectX: number;
  private readonly contactX: number;
  private readonly spawnX: number;
  private readonly groundY: number;

  constructor(
    scene: Phaser.Scene,
    container: Phaser.GameObjects.Container,
    rect: CombatRect,
    options: WaveEngineOptions
  ) {
    this.scene = scene;
    this.container = container;
    this.wave = options.wave;
    this.stats = options.stats;
    this.isBossWave = options.def.isBoss ?? false;

    this.groundY = rect.y + rect.height * 0.78;
    this.heroIdleX = rect.x + rect.width * 0.16;
    this.contactX = this.heroIdleX + rect.width * 0.14;
    this.spawnX = rect.x + rect.width - 8;
    this.heroCollectX = rect.x + rect.width * 0.62;

    this.rng = new Rng((Date.now() ^ (options.wave * 0x9e3779b9)) >>> 0);
    this.damageNumbers = new DamageNumbers(scene, container);
    this.hero = new Hero(scene, container, {
      x: this.heroIdleX,
      y: this.groundY,
      hp: options.hp,
      maxHp: options.maxHp,
      attackIntervalMs: HERO_ATTACK_INTERVAL_MS,
    });

    const plan = buildWaveSpawnPlan(options.def, this.rng);
    this.pendingSpawns = plan.spawns;

    if (this.isBossWave) {
      const boss = plan.spawns[0];
      this.bossController = new BossController(scene, container, {
        x: rect.x + rect.width * 0.08,
        y: rect.y + 14,
        width: rect.width * 0.6,
        name: bossDisplayName(boss?.type ?? 'jefe'),
        hp: boss ? boss.hp : 1,
      });
    }

    if (options.hp <= 0) {
      this.state = 'dead';
    }
  }

  /** Actualiza stats en caliente (p.ej. tras comprar una carta entre oleadas). */
  setStats(stats: RunStats): void {
    this.stats = stats;
  }

  isActive(): boolean {
    return this.state === 'active' || this.state === 'collecting';
  }

  get isDead(): boolean {
    return this.state === 'dead';
  }

  /** Vida actual del héroe (absoluta), para persistirla entre oleadas. */
  get heroHp(): number {
    return this.hero.hp;
  }

  /** Vida máxima efectiva del héroe en esta oleada. */
  get heroMaxHp(): number {
    return this.hero.maxHp;
  }

  /** Revive al héroe con la vida indicada y reanuda la oleada en curso. */
  revive(hp: number): void {
    this.hero.setMaxHp(this.hero.maxHp, hp);
    if (this.state === 'dead') {
      this.state = 'active';
    }
  }

  update(deltaMs: number): void {
    if (this.state === 'active') {
      this.updateActive(deltaMs);
    } else if (this.state === 'collecting') {
      this.updateCollecting(deltaMs);
    }
  }

  private updateActive(deltaMs: number): void {
    this.elapsedMs += deltaMs;

    while (this.pendingSpawns.length > 0 && spawnDelayMs(this.pendingSpawns[0]!.order) <= this.elapsedMs) {
      this.spawnEnemy(this.pendingSpawns.shift()!);
    }

    for (const enemy of this.enemies) {
      const shouldAttack = enemy.update(deltaMs);
      if (shouldAttack) {
        this.resolveEnemyHit(enemy);
        if (this.state === 'dead') return;
      }
    }

    this.hero.updateAnim(deltaMs);
    if (this.hero.tickAttackCooldown(deltaMs)) {
      this.resolveHeroVolley();
    }

    if (this.pendingSpawns.length === 0 && this.enemies.length === 0) {
      this.beginCollecting();
    }
  }

  private spawnEnemy(spawn: SpawnInstance): void {
    const enemy = new Enemy(this.scene, this.container, {
      x: this.spawnX,
      y: this.groundY,
      type: spawn.type,
      hp: spawn.hp,
      atk: spawn.atk,
      speedPxPerSec: spawn.speed,
      attackIntervalMs: ENEMY_ATTACK_INTERVAL_MS,
      contactX: this.contactX,
      textureKey: textureForType(spawn.type),
    });
    this.enemies.push(enemy);
  }

  private resolveEnemyHit(enemy: Enemy): void {
    const result = resolveEnemyAttack(enemy.atk, this.stats, () => this.rng.float());
    if (result.blocked) {
      this.hero.flashShield();
      this.damageNumbers.spawnText(this.hero.x, this.hero.y - 30, 'bloqueado', '#8ecbff');
      return;
    }
    if (result.missed) {
      this.damageNumbers.spawnText(this.hero.x, this.hero.y - 30, 'falló', '#a99fd1');
      return;
    }
    this.hero.takeDamage(result.damage);
    this.hero.playHitFlash();
    audio.playHit(true);
    this.damageNumbers.spawn(this.hero.x, this.hero.y - 30, result.damage, { color: '#ff6b6b' });
    gameEvents.emit('combat:heroDamaged', { hp: this.hero.hp, maxHp: this.hero.maxHp });
    if (this.hero.isDead) {
      this.state = 'dead';
      gameEvents.emit('combat:heroDied', {});
    }
  }

  private resolveHeroVolley(): void {
    if (this.enemies.length === 0) return;
    const targets = [...this.enemies].sort((a, b) => a.x - b.x);
    const result = resolveHeroAttack(HERO_BASE_DAMAGE, this.stats, () => this.rng.float());
    audio.playShoot();
    this.hero.playAttackAnim();

    let cursor = 0;
    for (const hit of result.hits) {
      if (targets.length === 0) break;
      const target = targets[cursor % targets.length]!;
      cursor++;
      if (target.isDead) continue;

      target.takeDamage(hit.damage);
      target.playHitFlash();
      audio.playHit(false);
      this.damageNumbers.spawn(target.x, target.y - 32, hit.damage, { crit: hit.isCrit });
      if (this.bossController && target.type.includes('boss')) {
        this.bossController.setHp(target.hp);
      }
      if (target.isDead) {
        this.handleEnemyDeath(target);
      }
    }

    const heal = computeLifesteal(result.totalDamage, this.stats);
    if (heal > 0) {
      this.hero.heal(heal);
    }
  }

  private handleEnemyDeath(enemy: Enemy): void {
    const balls = this.isBossWave ? bossBallsDropped(enemy.maxHp) : ballsDropped(enemy.maxHp);
    if (this.isBossWave && enemy.type.includes('boss')) {
      // shake suave de cámara: remata la muerte del jefe con un poco más de peso.
      this.scene.cameras.main.shake(280, 0.006);
    }
    this.spawnPickup(enemy.x, enemy.y);
    gameEvents.emit('combat:enemyKilled', { type: enemy.type, x: enemy.x, balls });
    this.enemies = this.enemies.filter((e) => e !== enemy);
    enemy.destroy();
  }

  private spawnPickup(x: number, y: number): void {
    const image = this.scene.add.image(x, y - 6, TEX.ball);
    this.container.add(image);
    this.pickups.push({ image, order: this.pickups.length });
  }

  private beginCollecting(): void {
    this.state = 'collecting';
    this.collectElapsedMs = 0;
    this.collectDurationMs = Phaser.Math.Clamp(500 + this.pickups.length * 150, 500, 1800);
  }

  private updateCollecting(deltaMs: number): void {
    this.collectElapsedMs += deltaMs;
    const t = Phaser.Math.Clamp(this.collectElapsedMs / this.collectDurationMs, 0, 1);
    // el héroe avanza a recoger las bolas y vuelve a su posición ("ida y vuelta").
    const forward = t < 0.5 ? t * 2 : 1 - (t - 0.5) * 2;
    const x = Phaser.Math.Linear(this.heroIdleX, this.heroCollectX, forward);
    this.hero.setPosition(x, this.groundY);

    const total = Math.max(1, this.pickups.length);
    for (const pickup of this.pickups) {
      if (!pickup.image.active) continue;
      const threshold = pickup.order / total;
      if (t >= threshold) {
        const fade = Phaser.Math.Clamp((t - threshold) * 4, 0, 1);
        pickup.image.setAlpha(1 - fade);
        if (fade >= 1) {
          pickup.image.destroy();
        }
      }
    }

    if (t >= 1) {
      this.finishCollecting();
    }
  }

  private finishCollecting(): void {
    for (const pickup of this.pickups) {
      if (pickup.image.active) pickup.image.destroy();
    }
    this.pickups = [];
    this.hero.setPosition(this.heroIdleX, this.groundY);
    this.state = 'cleared';
    gameEvents.emit('combat:waveCleared', { wave: this.wave });
  }

  destroy(): void {
    for (const enemy of this.enemies) enemy.destroy();
    this.enemies = [];
    for (const pickup of this.pickups) {
      if (pickup.image.active) pickup.image.destroy();
    }
    this.pickups = [];
    this.hero.destroy();
    this.bossController?.destroy();
  }
}
